import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardHeader,
  CCardBody,
  CRow,
  CCol,
  CSpinner,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CButton,
  CBadge,
  CListGroup,
  CListGroupItem,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from "@coreui/react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getStoreDetails, getStoreVisitations, getStoreStockHistory, getStockList } from "src/services/Stores";
import formatter from "src/reusables/formatter";
import { DataGrid } from "@mui/x-data-grid";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilPencil, cilMap, cilPhone, cilCalendar, cilUser } from "@coreui/icons";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});
function StoreDetails() {
  const [storeDetails, setStoreDetails] = useState(null);
  const [visitationHistory, setVisitationHistory] = useState([]);
  const [loading, setLoading] = useState({
    main: true,
    visitations: false,
    stock: false,
    stockList: false,
  });
  const [activeTab, setActiveTab] = useState("visitations"); // Default to first tab
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [stocks, setStocks] = useState([]);
  const [stockList, setStockList] = useState([]);
  const [overallAvailability, setOverallAvailability] = useState(0);

  async function fetchStoreDetails() {
    try {
      const res = await getStoreDetails(id);
      setStoreDetails(res.store); // Access the store object from response
    } catch (error) {
      console.error("Error fetching store details:", error);
    } finally {
      setLoading((prev) => ({ ...prev, main: false }));
    }
  }
  function handleVisitationRowClick(params) {
    navigate(`/visitations/details/${params.row.id}`);
  }
  const visitationColumns = [
    {
      field: "created_at",
      headerName: "Date",
      flex: 1,
      valueFormatter: (params) => formatter("dateOnly", params.value),
    },
    {
      field: "userName",
      headerName: "SMD",
      flex: 1,
    },
    {
      field: "check_in",
      headerName: "Check In",
      flex: 1,
      renderCell: (params) => params.value || <CBadge color="secondary">Not checked in</CBadge>,
    },
    {
      field: "check_out",
      headerName: "Check Out",
      flex: 1,
      renderCell: (params) => params.value || <CBadge color="secondary">Not checked out</CBadge>,
    },
    {
      field: "duration",
      headerName: "Duration (min)",
      width: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "visibility",
      headerName: "Visibility",
      width: 120,
      renderCell: (params) => (
        <CBadge color={params.value === "High" ? "success" : params.value === "Medium" ? "warning" : "danger"}>
          {params.value}
        </CBadge>
      ),
    },
  ];
  // ... keep your existing fetchVisitationHistory and fetchStockHistory functions ...

  useEffect(() => {
    fetchStoreDetails();
    fetchVisitationHistory();
    fetchStockList();
  }, [id]);
  const handleTabChange = (tab) => {
    setActiveTab(tab);

    // Fetch data only when tab is clicked
    if (tab === "visitations" && visitationHistory.length === 0) {
      fetchVisitationHistory();
    } else if (tab === "stock" && stocks.length === 0) {
      fetchStockHistory();
    }
  };
  // ... keep your existing tab change handler and column definitions ...
  async function fetchVisitationHistory() {
    try {
      setLoading((prev) => ({ ...prev, visitations: true }));
      const res = await getStoreVisitations(id);
      setVisitationHistory(res.visitations || []);
    } catch (error) {
      console.error("Error fetching visitation history:", error);
    } finally {
      setLoading((prev) => ({ ...prev, visitations: false }));
    }
  }

  async function fetchStockHistory() {
    try {
      setLoading((prev) => ({ ...prev, stock: true }));
      const res = await getStoreStockHistory(id);
      setStocks(res);
    } catch (error) {
      console.error("Error fetching stock history:", error);
    } finally {
      setLoading((prev) => ({ ...prev, stock: false }));
    }
  }

  function transformStockData(products) {
    // Group products by category_id
    const categoryMap = {};
    for (const product of products) {
      const categoryID = product.category_id;
      if (!categoryMap[categoryID]) {
        categoryMap[categoryID] = {
          categoryID: categoryID,
          categoryName: product.category_name,
          products: [],
        };
      }
      categoryMap[categoryID].products.push({
        productID: product.product_id,
        productName: product.product_name,
        stock: product.stock,
        isAvailable: product.availability === "available",
      });
    }
  
    // Calculate stats for each category
    const result = Object.values(categoryMap).map(category => {
      const availableProducts = category.products.filter(p => p.isAvailable).length;
      const totalProducts = category.products.length;
      const availabilityPercentage = totalProducts > 0 ? Math.round((availableProducts / totalProducts) * 100) : 0;
      return {
        ...category,
        availableProducts,
        totalProducts,
        availabilityPercentage,
      };
    });
  
    // Sort by categoryID descending
    result.sort((a, b) => b.categoryID - a.categoryID);
  
    return result;
  }

  async function fetchStockList() {
    try {
      setLoading((prev) => ({ ...prev, stockList: true}));
      const response = await getStockList(id);
      setStockList(transformStockData(response.data.products));
      setOverallAvailability(response.data.availability_percentage);
    } catch (e) {
      console.error("Error fetching stock list:", e);
    } finally {
      setLoading((prev) => ({ ...prev, stockList: false}))
    }
  }

  if (loading.main) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  if (!storeDetails) {
    return <div>Store not found</div>;
  }

  // Sort categories by categoryID descending
  const sortedStockList = [...stockList].sort(
    (a, b) => b.categoryID - a.categoryID
  );

  return (
    <div style={{ color: "black" }}>
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              <h4 className="mb-0">
                {storeDetails.store_name}
                <CBadge color="primary" className="ms-2">
                  {storeDetails.store_type}
                </CBadge>
              </h4>
              <small className="text-muted">ID: {storeDetails.store_id}</small>
            </CCol>
            <CCol md={6} className="text-end">
              <CButton
                color="light"
                onClick={() =>
                  navigate(-1, {
                    state: {
                      page: location.state?.fromPage || 0,
                      pageSize: location.state?.fromPageSize || 10,
                    },
                  })
                }
                className="me-2"
              >
                <CIcon icon={cilArrowLeft} className="me-2" />
                Back
              </CButton>
              <CButton
                color="primary"
                onClick={() => navigate(`/stores/actions/${id}`)}
              >
                <CIcon icon={cilPencil} className="me-2" />
                Edit Store
              </CButton>
            </CCol>
          </CRow>
        </CCardHeader>

        <CCardBody>
          <CRow className="mb-4">
            <CCol md={6}>
              <CCard className="mb-4">
                <CCardHeader>
                  <h5>Store Information</h5>
                </CCardHeader>
                <CCardBody>
                  <CListGroup>
                    <CListGroupItem className="d-flex align-items-center">
                      <CIcon icon={cilMap} className="me-3 text-primary" />
                      <div>
                        <strong>Address:</strong>
                        <br />
                        {storeDetails.address}
                        <br />
                        {storeDetails.districtName}, {storeDetails.cityName}
                        <br />
                        {storeDetails.provinceName}
                      </div>
                    </CListGroupItem>
                    <CListGroupItem className="d-flex align-items-center">
                      <CIcon icon={cilPhone} className="me-3 text-primary" />
                      <div>
                        <strong>Phone:</strong> {storeDetails.phone}
                      </div>
                    </CListGroupItem>
                    <CListGroupItem className="d-flex align-items-center">
                      <CIcon icon={cilCalendar} className="me-3 text-primary" />
                      <div>
                        <strong>Open Date:</strong>{" "}
                        {formatter("dateOnly", storeDetails.open_date)}
                      </div>
                    </CListGroupItem>
                  </CListGroup>
                </CCardBody>
              </CCard>

              <CCard>
                <CCardHeader>
                  <h5>Location Details</h5>
                </CCardHeader>
                <CCardBody>
                  <CRow className="mb-3">
                    <CCol md={6}>
                      <p>
                        <strong>Latitude:</strong> {storeDetails.latitude}
                      </p>
                    </CCol>
                    <CCol md={6}>
                      <p>
                        <strong>Longitude:</strong> {storeDetails.longitude}
                      </p>
                    </CCol>
                  </CRow>

                  {storeDetails.latitude && storeDetails.longitude && (
                    <div
                      style={{
                        height: "300px",
                        borderRadius: "0.375rem",
                        overflow: "hidden",
                      }}
                    >
                      <MapContainer
                        center={[
                          parseFloat(storeDetails.latitude),
                          parseFloat(storeDetails.longitude),
                        ]}
                        zoom={15}
                        scrollWheelZoom={false}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker
                          position={[
                            parseFloat(storeDetails.latitude),
                            parseFloat(storeDetails.longitude),
                          ]}
                        >
                          <Popup>
                            <strong>{storeDetails.name}</strong>
                            <br />
                            {storeDetails.address}
                          </Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>

            <CCol md={6}>
              <CCard className="mb-4">
                <CCardHeader>
                  <h5>Contact Persons</h5>
                </CCardHeader>
                <CCardBody>
                  <CListGroup>
                    <CListGroupItem>
                      <div className="d-flex align-items-center mb-2">
                        <CIcon icon={cilUser} className="me-3 text-primary" />
                        <h6 className="mb-0">Vaporista</h6>
                      </div>
                      <p className="mb-1">
                        <strong>Name:</strong> {storeDetails.vaporista_name}
                      </p>
                      <p className="mb-1">
                        <strong>Phone:</strong> {storeDetails.vaporista_phone}
                      </p>
                      <p className="mb-0">
                        <strong>Birthday:</strong>{" "}
                        {formatter("dateOnly", storeDetails.vaporista_birthday)}
                      </p>
                    </CListGroupItem>
                    <CListGroupItem>
                      <div className="d-flex align-items-center mb-2">
                        <CIcon icon={cilUser} className="me-3 text-primary" />
                        <h6 className="mb-0">Owner</h6>
                      </div>
                      <p className="mb-1">
                        <strong>Name:</strong> {storeDetails.owner_name}
                      </p>
                      <p className="mb-1">
                        <strong>Phone:</strong> {storeDetails.owner_phone}
                      </p>
                      <p className="mb-0">
                        <strong>Birthday:</strong>{" "}
                        {formatter("dateOnly", storeDetails.owner_birthday)}
                      </p>
                    </CListGroupItem>
                    <CListGroupItem>
                      <div className="d-flex align-items-center">
                        <CIcon icon={cilUser} className="me-3 text-primary" />
                        <div>
                          <p className="mb-1">
                            <strong>Upline:</strong> {storeDetails.upline_name}
                          </p>
                        </div>
                      </div>
                    </CListGroupItem>
                  </CListGroup>
                </CCardBody>
              </CCard>

              <CCard>
                <CCardHeader>
                  <h5>Last Activity</h5>
                </CCardHeader>
                <CCardBody>
                  <p>
                    <strong>Last Visitation:</strong>{" "}
                    {storeDetails.last_visited
                      ? formatter("completeDate", storeDetails.last_visited)
                      : "No record"}
                    {/* {storeDetails.last_visited ? formatter("datetime", storeDetails.last_visited) : "No record"} */}
                  </p>
                  {/* Add more activity metrics here as needed */}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>

          <CNav variant="tabs">
            <CNavItem>
              <CNavLink
                active={activeTab === "visitations"}
                onClick={() => handleTabChange("visitations")}
              >
                Visitation History
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                active={activeTab === "stockList"}
                onClick={() => handleTabChange("stockList")}
              >
                Stock List
              </CNavLink>
            </CNavItem>
          </CNav>
          <CTabContent>
            <CTabPane visible={activeTab === "visitations"}>
              {loading.visitations ? (
                <div className="text-center py-4">
                  <CSpinner color="primary" />
                </div>
              ) : (
                <div style={{ height: 400, width: "100%", marginTop: "16px" }}>
                  <DataGrid
                    rows={visitationHistory}
                    columns={visitationColumns}
                    onRowClick={handleVisitationRowClick}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading.visitations}
                    disableSelectionOnClick
                    sx={{
                      "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                      },
                      "& .MuiDataGrid-columnHeader:focus": {
                        outline: "none",
                      },
                    }}
                  />
                </div>
              )}
            </CTabPane>

            <CTabPane visible={activeTab === "stockList"}>
              <CCard className="mb-4">
                <CCardHeader>
                  <strong>Stock Produk</strong>
                </CCardHeader>
                <CCardBody>
                  {/* Stock Availability Summary */}
                  {sortedStockList.length > 0 && (
                    <div className="mb-4">
                      <h5>Ringkasan Ketersediaan Stok</h5>
                      <CRow className="g-3 mb-4">
                        {sortedStockList.map((category) => (
                          <CCol key={`summary-${category.categoryID}`} md={6} lg={3}>
                            <CCard
                              className="h-100"
                              style={{
                                cursor: "pointer",
                                borderLeft: `4px solid ${
                                  category.availabilityPercentage >= 50
                                    ? "#2eb85c"
                                    : category.availabilityPercentage >= 25
                                    ? "#f9b115"
                                    : "#e55353"
                                }`,
                              }}
                              onClick={() => {
                                // Scroll to the category section
                                document.getElementById(`category-${category.categoryID}`)?.scrollIntoView({
                                  behavior: "smooth",
                                });
                              }}
                            >
                              <CCardBody className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 className="mb-1">{category.categoryName}</h6>
                                    <small className="text-muted">
                                      {category.availableProducts} dari {category.totalProducts} produk tersedia
                                    </small>
                                  </div>
                                  <CBadge
                                    color={
                                      category.availabilityPercentage >= 50
                                        ? "success"
                                        : category.availabilityPercentage >= 25
                                        ? "warning"
                                        : "danger"
                                    }
                                    shape="rounded-pill"
                                    className="fs-6"
                                  >
                                    {category.availabilityPercentage}%
                                  </CBadge>
                                </div>
                                <div className="mt-2">
                                  <div className="progress" style={{ height: "8px" }}>
                                    <div
                                      className={`progress-bar ${
                                        category.availabilityPercentage >= 50
                                          ? "bg-success"
                                          : category.availabilityPercentage >= 25
                                          ? "bg-warning"
                                          : "bg-danger"
                                      }`}
                                      role="progressbar"
                                      style={{ width: `${category.availabilityPercentage}%` }}
                                      aria-valuenow={category.availabilityPercentage}
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    ></div>
                                  </div>
                                </div>
                              </CCardBody>
                            </CCard>
                          </CCol>
                        ))}
                      </CRow>
                    </div>
                  )}

                  {/* Detailed Stock Table */}
                  {sortedStockList.length > 0 ? (
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      {sortedStockList.map((category, idx) => (
                        <div key={category.categoryID} id={`category-${category.categoryID}`} className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5>{category.categoryName}</h5>
                            <CBadge
                              color={
                                category.availabilityPercentage >= 50
                                  ? "success"
                                  : category.availabilityPercentage >= 25
                                  ? "warning"
                                  : "danger"
                              }
                            >
                              Ketersediaan: {category.availabilityPercentage}%
                            </CBadge>
                          </div>
                          <CTable striped hover responsive>
                            <CTableHead>
                              <CTableRow>
                                <CTableHeaderCell>Produk</CTableHeaderCell>
                                <CTableHeaderCell>Stock</CTableHeaderCell>
                                <CTableHeaderCell>Status</CTableHeaderCell>
                              </CTableRow>
                            </CTableHead>
                            <CTableBody>
                              {[...category.products]
                                .sort((a, b) => a.productName.localeCompare(b.productName))
                                .map((product) => (
                                  <CTableRow key={product.productID}>
                                    <CTableDataCell>{product.productName}</CTableDataCell>
                                    <CTableDataCell>
                                      <CBadge color={product.stock > 0 ? "success" : "danger"}>{product.stock}</CBadge>
                                    </CTableDataCell>
                                    <CTableDataCell>
                                      <CBadge color={product.isAvailable ? "success" : "secondary"}>
                                        {product.isAvailable ? "Tersedia" : "Tidak Tersedia"}
                                      </CBadge>
                                    </CTableDataCell>
                                  </CTableRow>
                                ))}
                            </CTableBody>
                          </CTable>
                          {idx < sortedStockList.length - 1 && <hr />} {/* Divider between categories */}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <CBadge color="secondary">Tidak ada data stock</CBadge>
                  )}
                </CCardBody>
              </CCard>
            </CTabPane>
          </CTabContent>
        </CCardBody>
      </CCard>
    </div>
  );
}

export default StoreDetails;
